import { Component, Input, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { FallbackCellType, VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { VideoEventCode } from "@app/main/components/videos/enums/VideoEventCode";
import { VideoStatus } from "@app/main/components/videos/enums/VideoStatus";
import { IVideoEntry } from "@app/main/components/videos/models/IVideoEntry";
import { FoundationVideoService } from "@app/main/modules/foundation/_services/video.service";
import { DateService } from "@app/services/date.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ITimelineSummary } from "@app/main/components/videos/models/CameraFootageTimeline.interfaces";
import { VideoStreamType } from "@app/main/components/videos/enums/VideoStreamType";
import { IAvailableDailyFootage } from "@app/main/components/videos/models/IAvailableDailyFootage";
import { VideoEntryService } from "@app/main/components/videos/services/video-entry.service";
import { IVideoStream } from "@app/main/components/videos/models/ICameraChannel";
import { CameraFootageTimelineService } from "@app/main/components/videos/services/camera-footage-timeline.service";
import './camera-videos.component.scss';
import { IJourney } from "@app/interfaces/IJourney";

@UntilDestroy()
@Component({
    selector: 'camera-videos',
    template: require('./camera-videos.component.html')
})
export class CameraVideosComponent implements OnInit {
    @Input() camera: IPeripheral;

    videos: IVideoEntry[];

    tableColumns: IVTableColumn<IVideoEntry>[];
    tableRows: IVTableRow[];

    startDate: Date;
    endDate: Date;
    defaultDateRange = DateRange.Last30Days;

    selectedVideo: IVideoEntry;
    VideoEventCode = VideoEventCode;
    VideoStatus = VideoStatus;

    loading: boolean;

    showRequestVideo: boolean = false;

    minDate: Date;
    selectedDate: Date;
    selectedSecond: number;

    timelineSummary: ITimelineSummary;

    selectedChannels: number[];
    videoDuration: number;

    availableDailyFootage: IAvailableDailyFootage[] = [];
    journeys: IJourney[] = [];

    get isRequestDisabled(): boolean {
        const startTimestamp = new Date(this.selectedDate).getTime();
        const endTimestamp = startTimestamp + this.videoDuration * 1000;
        const now = new Date().getTime();

        const isInside = this.timelineSummary?.displaySections.some(section => {
            const sectionStart = new Date(section.dateStart).getTime();
            const sectionEnd = new Date(section.dateEnd) ? new Date(section.dateEnd).getTime() : now;

            return startTimestamp >= sectionStart && endTimestamp <= sectionEnd;
        });

        return !isInside;
    }

    get showActivityChart(): boolean {
        return this.timelineSummary?.displaySections?.length > 0;
    }

    constructor(
        private readonly dateService: DateService,
        private readonly videoService: FoundationVideoService,
        private readonly vTableService: VTableService,
        private readonly videoEntryService: VideoEntryService,
        private readonly cameraFootageTimelineService: CameraFootageTimelineService) { }

    ngOnInit(): void {
        this.setDefaultDates();
        this.createTable();
    }

    onRowClicked(row: IVTableRow): void {
        this.selectedVideo = row.data;
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<IVideoEntry>): void {
        if (filterEvent.trigger == "date") {
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];
            this.getVideos();
        }
    }

    clearSelectedVideo(): void {
        this.selectedVideo = null;
    }

    onRequestVideo(): void {
        this.showRequestVideo = true;
    }

    closeRequestVideo(): void {
        this.showRequestVideo = false;
    }

    onFootageHistoryLoaded(availableDailyFootage: IAvailableDailyFootage[]): void {            
        this.availableDailyFootage = availableDailyFootage.map(item => ({
            ...item,
            Start: new Date(item.Start),
            End: new Date(item.End)
        }));

        this.selectedDate = availableDailyFootage.length > 0 ? availableDailyFootage[0].Start : new Date();
        this.selectedSecond = this.dateService.getSecondOfDay(this.selectedDate) + 1;

        this.timelineSummary = this.cameraFootageTimelineService.createAvailableDailyFootageTimeline(this.selectedDate, availableDailyFootage.map(item => ({
            ...item,
            Start: new Date(item.Start),
            End: new Date(item.End)
        })));
        
    }

    onJourneysLoaded(journeys: IJourney[]): void {        
        this.journeys = journeys;

        this.selectedDate = this.journeys.length > 0 ? new Date(this.journeys[0].StartUtc) : new Date();
        this.selectedSecond = this.dateService.getSecondOfDay(this.selectedDate) + 1;

        this.timelineSummary = this.cameraFootageTimelineService.createAvailableDailyFootageTimelineFromJourneys(this.selectedDate, this.journeys);
    }

    onDateChanged($event: any): void {
        
        this.selectedDate = new Date($event);
        this.selectedSecond = this.dateService.getSecondOfDay(this.selectedDate) + 1;
        
        
        if (this.availableDailyFootage.length > 0) {
            const footagecopy = this.availableDailyFootage.map(item => ({
                ...item,
                Start: new Date(item.Start),
                End: new Date(item.End)
            }))

            this.timelineSummary = this.cameraFootageTimelineService.createAvailableDailyFootageTimeline(this.selectedDate, footagecopy);
        }
        else if (this.journeys.length > 0) {
            this.timelineSummary = this.cameraFootageTimelineService.createAvailableDailyFootageTimelineFromJourneys(this.selectedDate, this.journeys);
        }
    }

    onNewSecondOfDaySelected($event: any): void {
        this.selectedDate = this.dateService.getDateTimeFromSecondOfDay($event, this.selectedDate);
    }

    onSelectedChannelChanged(channels: IVideoStream[]): void {
        this.selectedChannels = [];
        channels.forEach(channel => {
            this.selectedChannels.push(channel.ChannelId)
        });
    }

    onVideoLengthChanged(duration: number): void {
        this.videoDuration = duration;
    }

    requestVideo(): void {
        this.loading = true;
        this.videoEntryService.requestVideoUpload({
            VehicleId: this.camera.vehicleMapping?.vehicle?.Id,
            Length: this.videoDuration,
            VideoStartTime: this.selectedDate,
            Channels: this.selectedChannels,
            VideoId: null,
            StreamType: VideoStreamType.Main,
            IsFoundationRequest: true
        }).finally(() => {
            this.loading = false;
            this.showRequestVideo = false;
        })
    }

    //--

    private getVideos(): void {
        if (!this.camera) {
            return;
        }

        this.loading = true;

        this.videoService.getVideoEntries({ VehicleId: this.camera.vehicleMapping?.vehicle?.Id, Start: this.startDate, End: this.endDate, UseCreatedDate: true }).pipe(
            untilDestroyed(this)
        ).subscribe({
            next: videos => {
                this.videos = videos;
            },
            complete: () => {
                this.tableRows = this.vTableService.createTableRows(this.videos, this.tableColumns);
                this.loading = false
            },
            error: () => this.loading = false
        })
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CameraVideos.VideoStartTime", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: video => this.vTableService.createDateCell(video.VideoStartTime)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CameraVideos.VideoEndTime", sorted: true }),
                createCellFunction: video => this.vTableService.createDateCell(video.VideoEndTime)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CameraVideos.Duration" }),
                createCellFunction: video => this.vTableService.createDurationCell(new Date(video.VideoEndTime).getTime() - new Date(video.VideoStartTime).getTime())
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: video => this.vTableService.createVehicleRegCell(this.camera.vehicleMapping?.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CameraVideos.EventCode" }),
                createCellFunction: video => this.vTableService.createEnumCell(VideoEventCode, "VideoEventCode", video.EventCode)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status" }),
                createCellFunction: video => this.vTableService.createEnumCell(VideoStatus, "VideoStatus", video.Status)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CameraVideos.UserRequestedDate", width: 200 }),
                createCellFunction: video => this.vTableService.createDateCell(video.DateRequested, { dateTime: true })
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CameraVideos.CameraRequestedDate", width: 200 }),
                createCellFunction: video => this.vTableService.createDateCell(video.MetaData.CameraUploadRequestedTime)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "CameraVideos.ReceivedDate", width: 200 }),
                createCellFunction: video => this.vTableService.createDateCell(video.DateDownloaded)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CameraVideos.ExternalId" }),
                createCellFunction: video => this.vTableService.createTableCell(video.ExternalId, undefined, FallbackCellType.Dashed)
            }
        ]
    }

    private setDefaultDates(): void {
        const dateRange = this.dateService.dateRangeFromEnum(this.defaultDateRange);
        this.startDate = dateRange.start;
        this.endDate = dateRange.end;
    }

}
